import {
    getR2UploadUrl,
    initiateMultipartUpload,
    getMultipartUploadUrls,
    completeMultipartUpload
} from './firebase-api';

interface UploadOptions {
    file: File | Blob;
    type: 'audio' | 'video' | 'image';
    onProgress?: (progress: number) => void;
    logFn?: (msg: string) => void;
}

/**
 * Uploads a file to R2 using the most efficient method (Simple or Multipart).
 * Handles large files with concurrent chunk uploads.
 */
export const uploadFileToR2 = async ({ file, type, onProgress, logFn }: UploadOptions): Promise<string> => {
    const isFile = file instanceof File;
    const fileName = isFile ? (file as File).name : `${type}_extract_${Date.now()}.${type === 'audio' ? 'mp3' : 'mp4'}`;
    const fileSize = file.size;
    const ext = isFile ? fileName.split('.').pop() || 'mp4' : 'mp3';
    // Ensure correct content type
    const contentType = type === 'audio'
        ? 'audio/mpeg'
        : (isFile ? (file as File).type : 'video/mp4') || 'video/mp4';

    const log = (msg: string) => {
        if (logFn) logFn(msg);
        else console.log(`[UploadManager] ${msg}`);
    };

    // Dynamic Chunk Configuration
    let CHUNK_SIZE = 10 * 1024 * 1024; // Default 10MB
    let CONCURRENT_UPLOADS = 3;

    // For files larger than 1GB, optimize for speed
    if (fileSize > 1024 * 1024 * 1024) {
        CHUNK_SIZE = 50 * 1024 * 1024; // 50MB
        CONCURRENT_UPLOADS = 5;       // More parallelism
        log(`🚀 Mode Ultra-Fast activé (Chunks: 50MB, Threads: 5)`);
    }

    // --- SIMPLE UPLOAD (Single Request) ---
    if (fileSize <= CHUNK_SIZE) {
        const uniqueFileName = `${type}_${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
        log(`Simple upload for ${fileName} (${(fileSize / 1024 / 1024).toFixed(2)} MB)`);

        const result = await getR2UploadUrl({ fileName: uniqueFileName, contentType });
        const { uploadUrl, publicUrl } = (result.data as any);

        await new Promise<void>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', uploadUrl, true);
            xhr.setRequestHeader('Content-Type', contentType);
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable && onProgress) {
                    onProgress(Math.round((e.loaded / e.total) * 100));
                }
            };
            xhr.onload = () => xhr.status === 200 ? resolve() : reject(new Error(`Status ${xhr.status}`));
            xhr.onerror = () => reject(new Error('Network Error'));
            xhr.send(file);
        });

        if (onProgress) onProgress(100);
        return publicUrl;
    }

    // --- MULTIPART UPLOAD (Concurrent Chunks) ---
    else {
        const totalParts = Math.ceil(fileSize / CHUNK_SIZE);
        log(`📦 Mode Multipart: ${totalParts} parties de ${(CHUNK_SIZE / 1024 / 1024).toFixed(0)}MB pour ${(fileSize / 1024 / 1024).toFixed(1)}MB`);

        // 1. Init
        // Use original name but ensure uniqueness if needed, or let server handle it?
        // Ideally we want a clean name.
        const uniqueFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
        const initRes = await initiateMultipartUpload({ fileName: uniqueFileName, contentType });
        const { uploadId, key } = (initRes.data as any);
        log(`ID Upload: ${uploadId}`);

        const uploadedParts: { ETag: string, PartNumber: number }[] = [];
        let partsCompleted = 0;

        // Process in batches
        for (let i = 0; i < totalParts; i += CONCURRENT_UPLOADS) {
            const batchIndices = [];
            for (let j = 0; j < CONCURRENT_UPLOADS && (i + j) < totalParts; j++) {
                batchIndices.push(i + j);
            }

            // Get URLs for this batch
            const partNumbers = batchIndices.map(idx => idx + 1);
            const urlsRes = await getMultipartUploadUrls({ key, uploadId, partNumbers });
            const { urls } = (urlsRes.data as any); // Array of { partNumber, url }

            // Upload batch in parallel
            await Promise.all(batchIndices.map(async (partIndex) => {
                const partNumber = partIndex + 1;
                const start = partIndex * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, fileSize);
                const chunk = file.slice(start, end);

                const signedUrlObj = urls.find((u: any) => u.partNumber === partNumber);
                if (!signedUrlObj) throw new Error(`Missing URL for part ${partNumber}`);

                // Retry logic for individual chunks
                let attempt = 0;
                const MAX_RETRIES = 3;

                while (attempt < MAX_RETRIES) {
                    try {
                        const response = await fetch(signedUrlObj.url, {
                            method: 'PUT',
                            body: chunk
                        });

                        if (!response.ok) throw new Error(`HTTP ${response.status}`);

                        const etag = response.headers.get('ETag');
                        if (!etag) throw new Error(`No ETag for part ${partNumber}`);

                        uploadedParts.push({ PartNumber: partNumber, ETag: etag.replace(/"/g, '') });
                        break; // Success
                    } catch (err) {
                        attempt++;
                        if (attempt >= MAX_RETRIES) throw err;
                        await new Promise(r => setTimeout(r, 1000 * attempt));
                    }
                }

                partsCompleted++;
                // Update Progress (Approximate)
                const percent = Math.round((partsCompleted / totalParts) * 100);
                if (onProgress) onProgress(percent);
            }));

            log(`✅ Batch ${Math.min(i + CONCURRENT_UPLOADS, totalParts)}/${totalParts} terminé`);
        }

        // 2. Complete
        log('Finalisation de l\'upload multipart...');
        // Sort parts just in case
        uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber);

        const completeRes = await completeMultipartUpload({ key, uploadId, parts: uploadedParts });
        const { publicUrl } = (completeRes.data as any);

        if (onProgress) onProgress(100);
        return publicUrl;
    }
};
